import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi, type AgentDigest, type AgentDigestCluster } from '../../api/adminApi';
import { Badge } from '@shared-ui/components';
import { sevLabel } from './severityBadge';

const VERDICT_VARIANT: Record<string, 'success' | 'error' | 'default'> = {
  verified: 'success', unverified: 'error', unchecked: 'default',
};

// Provenance is the "should I act on this?" signal — a harness/infra cluster is a bug in
// OUR tooling wearing a product bug's clothes, so it gets the loudest treatment.
const PROVENANCE_LABEL: Record<string, string> = {
  product: 'product', 'harness-suspect': 'harness?', 'agent-infra': 'agent infra', unknown: 'unplaced',
};
const PROVENANCE_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  product: 'success', 'harness-suspect': 'warning', 'agent-infra': 'error', unknown: 'default',
};

function ClusterCard({ c }: { c: AgentDigestCluster }) {
  const [open, setOpen] = useState(false);
  const v = c.verification;
  const suspect = v.provenance === 'harness-suspect' || v.provenance === 'agent-infra';
  return (
    <div className={`pa-cluster${suspect ? ' pa-cluster-suspect' : ''}`}>
      <div className="pa-cluster-head" onClick={() => setOpen(o => !o)}>
        <div className="pa-cluster-title">
          <Badge variant={PROVENANCE_VARIANT[v.provenance] ?? 'default'} size="sm">
            {PROVENANCE_LABEL[v.provenance] ?? v.provenance}
          </Badge>
          <Badge variant={VERDICT_VARIANT[v.verdict] ?? 'default'} size="sm">{v.verdict}</Badge>
          <strong>{c.theme}</strong>
        </div>
        <div className="pa-cluster-meta">
          {c.kind === 'issue' && (
            <><span title="how often it came up, weighted by how urgent it is — higher is more urgent">rank {c.rank}</span><span>·</span></>
          )}
          <span>{c.frequency}×</span>
          {c.kind === 'issue' && (
            <><span>·</span><span title="the worst finding in this cluster">sev {sevLabel(c.severityWorst)}</span></>
          )}
          {c.operation && <Badge variant="info" size="sm">{c.operation}</Badge>}
          <Badge variant="default" size="sm">{c.category}</Badge>
        </div>
      </div>
      <p className="pa-cluster-summary">{c.summary}</p>
      <p className="pa-cluster-verify"><em>{v.note}</em></p>
      {open && (
        <div className="pa-cluster-detail">
          {c.sampleObservations.length > 0 && (
            <>
              <div className="pa-cluster-label">Sample observations</div>
              <ul>{c.sampleObservations.map((o, i) => <li key={i}>{o}</li>)}</ul>
            </>
          )}
          {v.evidence && v.evidence.length > 0 && (
            <>
              <div className="pa-cluster-label">Evidence</div>
              <ul className="pa-mono">{v.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </>
          )}
          <div className="pa-muted">{c.findingIds.length} finding(s) in this cluster</div>
        </div>
      )}
    </div>
  );
}

export function DigestTab() {
  const qc = useQueryClient();
  const [windowDays, setWindowDays] = useState(7);
  const { data, isLoading } = useQuery({ queryKey: ['agent-digest'], queryFn: () => adminApi.getAgentDigest() });
  const digest: AgentDigest | null = data?.digest ?? null;
  const clusters = digest?.clusters ?? [];
  const issues = clusters.filter(c => c.kind !== 'praise');
  const praise = clusters.filter(c => c.kind === 'praise');
  const suspectCount = issues.filter(c =>
    c.verification.provenance === 'harness-suspect' || c.verification.provenance === 'agent-infra').length;

  const generate = useMutation({
    mutationFn: () => adminApi.generateAgentDigest(windowDays),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-digest'] }),
  });

  return (
    <>
      <div className="pa-filters">
        <span className="pa-muted">Cluster & verify findings from the last</span>
        <select className="pa-input" value={windowDays} onChange={e => setWindowDays(Number(e.target.value))}>
          {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
        </select>
        <button className="pa-btn" disabled={generate.isPending} onClick={() => generate.mutate()}>
          {generate.isPending ? 'Generating…' : 'Generate digest'}
        </button>
        {generate.isError && <span className="pa-muted" style={{ color: 'var(--color-error)' }}>generation failed — check the logs</span>}
      </div>

      {isLoading ? (
        <div className="pa-muted">Loading…</div>
      ) : !digest ? (
        <div className="pa-muted">No digest yet — click “Generate digest” to build one from recent findings.</div>
      ) : (
        <>
          <div className="pa-digest-meta">
            <strong>{digest.clusters.length}</strong> themes from <strong>{digest.findingCount}</strong> findings
            {' · '}generated {digest.generatedAt ? format(new Date(digest.generatedAt), 'MMM d HH:mm') : ''}
            {' · '}window {format(new Date(digest.windowStart), 'MMM d')}–{format(new Date(digest.windowEnd), 'MMM d')}
            {digest.model ? ` · ${digest.model}` : ''}
            {digest.notes ? <div className="pa-muted">{digest.notes}</div> : null}
            {suspectCount > 0 && (
              <div className="pa-digest-warn">
                {suspectCount} of {issues.length} issue theme(s) trace back to our own harness or agent infra — fix those before treating them as product bugs.
              </div>
            )}
          </div>
          <div className="pa-clusters">
            {issues.map(c => <ClusterCard key={c.id} c={c} />)}
          </div>
          {praise.length > 0 && (
            <>
              <div className="pa-cluster-section">Praise · not ranked</div>
              <div className="pa-clusters">
                {praise.map(c => <ClusterCard key={c.id} c={c} />)}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
